import moment from 'moment';
import Formatters from '@/shared/formatters/formatters';
import i18n from '@/plugins/i18n';

import {
  AdmissionMapFilter,
  AdmissionMapItem,
  AdmissionMapModel,
  AdmissionMapPagination,
  AdmissionPasswordStatus,
  AdmissionMapHistoryItem,
  AdmissionMapHistoryChange,
  AdmissionMapHistoryRevision,
  AdmissionMapHistoryOptions,
} from './admission-map-types';
import {
  formatHighCostHistoryValue,
  formatPasswordStatus,
  formatUrgencyTypeHistoryValue,
} from './admission-map-config-types';

const formatter = new Formatters();
const translate = (key: string) => String(i18n.t(key));

export const buildSearchParams = (data: AdmissionMapModel): Record<string, any> => {
  let params: Record<string, any> = {
    page: Math.max(data.pagination.page - 1, 0),
    size: data.pagination.itemsPerPage,
  }

  let filter = buildFilterParams(data);

  params = {
    ...params,
    ...filter
  }

  if (data.sortBy) {
    params.sort = `${data.sortBy},${data.sortDesc ? 'asc' : 'desc'}`;
  }

  return params;
};

export const buildFilterParams = (data: AdmissionMapModel): Record<string, any> => {
  const params: Record<string, any> = {};

  const {
    financialGroupId,
    carrierId,
    contractId,
    patientName,
    hasRecordHistory,
    passwordStatuses,
    startDate,
    endDate,
  } = data.filter;

  if (financialGroupId) params.financialGroupId = financialGroupId;
  if (carrierId) params.carrierId = carrierId;
  if (contractId) params.contractId = contractId;
  if (patientName) params.patientName = patientName;
  if (startDate) params.startDate = moment(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
  if (endDate) params.endDate = moment(endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
  if (typeof hasRecordHistory === 'boolean') params.hasRecordHistory = hasRecordHistory;
  if (passwordStatuses && passwordStatuses.length > 0) params.passwordStatuses = passwordStatuses.join(',');

  return params;
};

export const normalizePasswordStatus = (raw: unknown): AdmissionPasswordStatus | null => {
  if (!raw && raw !== false) return null;
  const value = String(raw).toUpperCase();
  const allowed: AdmissionPasswordStatus[] = ['LIBERADA', 'NEGADA', 'PENDENTE', 'CANCELADO', 'AUTORIZADO', 'CANCELADA', 'NEGADO', 'EM ANÁLISE'];
  if (allowed.includes(value as AdmissionPasswordStatus)) {
    return value as AdmissionPasswordStatus;
  } else {
    const text = String(value)
      .toLowerCase()
      .replace(/^./, char => char.toUpperCase());
    return text as AdmissionPasswordStatus;
  }
};

export const normalizeType = (value: unknown): string => {
  switch (value) {
    case 'ADD':
      return translate('admissionMap.history.types.added');
    case 'MOD':
      return translate('admissionMap.history.types.updated');
    case 'DEL':
      return translate('admissionMap.history.types.deleted');
    default:
      return String(value);
  }
};

export const normalizeAttributeName = (value: unknown): string => {
  switch (value) {
    case 'carrierName':
      return translate('admissionMap.fields.carrier');
    case 'contractName':
      return translate('admissionMap.fields.contract');
    case 'patientName':
      return translate('admissionMap.fields.patient');
    case 'passwordStatus':
      return translate('admissionMap.fields.passwordStatus');
    case 'cidCode':
      return translate('admissionMap.fields.cidCode');
    case 'cidDescription':
      return translate('admissionMap.fields.cidDescription');
    case 'speciality':
      return translate('admissionMap.fields.speciality');
    case 'urgencyType':
      return translate('admissionMap.fields.urgencyType');
    case 'highCost':
      return translate('admissionMap.fields.highCost');
    case 'unauthorized':
      return translate('admissionMap.fields.unauthorized');
    case 'authorizationStatus':
      return translate('admissionMap.fields.unauthorized');
    case 'icu':
      return translate('admissionMap.fields.icu');
    case 'medicalReport':
      return translate('admissionMap.fields.medicalReport');
    case 'specialCase':
      return translate('admissionMap.fields.specialCase');
    case 'evolution':
      return translate('admissionMap.fields.evolution');
    case 'note':
      return translate('admissionMap.fields.note');
    default:
      return String(value);
  }
};

export const normalizeAttributeValue = (value: unknown): string => {
  if (value === null) return '';
  const statusValue = String(value).toUpperCase();
  if (['LIBERADA', 'NEGADA', 'PENDENTE', 'CANCELADO', 'AUTORIZADO', 'CANCELADA', 'NEGADO'].includes(statusValue)) {
    return formatPasswordStatus(statusValue as AdmissionPasswordStatus);
  }

  const normalized = String(value)
    .toLowerCase()
    .replace(/^./, char => char.toUpperCase());

  if (normalized === 'True') return translate('admissionMap.boolean.yes');
  if (normalized === 'False') return translate('admissionMap.boolean.no');
  return normalized;
};

export const normalizeHistoryStatusValue = (value: unknown): string => {
  const raw = String(value);
  const upper = raw.toUpperCase();

  const negativeStatuses = new Set([
    'NEGADA',
    'NEGADO',
    'PENDENTE',
    'CANCELADO',
    'CANCELADA',
  ]);

  const positiveStatuses = new Set([
    'LIBERADA',
    'AUTORIZADO',
  ]);

  if (negativeStatuses.has(upper) || upper === 'TRUE') {
    return translate('admissionMap.boolean.yes');
  }

  if (positiveStatuses.has(upper) || upper === 'FALSE') {
    return translate('admissionMap.boolean.no');
  }

  return raw
    .toLowerCase()
    .replace(/^./, char => char.toUpperCase());
};

export const formatHistoryChangeValue = (
  attributeName: unknown,
  value: unknown,
  options: AdmissionMapHistoryOptions,
): string => {
  const attr = String(attributeName ?? '');
  if (attr === 'highCost') return formatHighCostHistoryValue(value, options.highCostTypes);
  if (attr === 'urgencyType') return formatUrgencyTypeHistoryValue(value, options.urgencyTypes);
  return normalizeHistoryStatusValue(value);
};

export const normalizeHistoryPayloadValue = (
  attributeName: string | null | undefined,
  value: unknown,
  options: AdmissionMapHistoryOptions,
): string | null => {
  if (value === null || value === undefined || value === '') return null;
  return formatHistoryChangeValue(attributeName, value, options);
};

export const normalizeHistory = (payload: any, options: AdmissionMapHistoryOptions): AdmissionMapHistoryItem[] => {
  const data = payload ?? [];
  try {
    const items: AdmissionMapHistoryItem[] = data.map((row: AdmissionMapHistoryItem) => ({
      carrierName: row.carrierName ?? null,
      contractName: row.contractName ?? null,
      patientName: row.patientName ?? null,
      revisions: (row.revisions ?? []).map((rev: AdmissionMapHistoryRevision) => ({
        revType: rev.revType ? normalizeType(rev.revType) : null,
        timestamp: rev.timestamp ? formatter.formatDateTimeZoneWithHours(rev.timestamp) : null,
        userName: rev.userName ?? null,
        userEmail: rev.userEmail ?? null,
        changes: (rev.changes ?? []).map((change: AdmissionMapHistoryChange) => ({
          attributeName: change.attributeName ? normalizeAttributeName(change.attributeName) : null,
          oldValue: normalizeHistoryPayloadValue(change.attributeName, change.oldValue, options),
          newValue: normalizeHistoryPayloadValue(change.attributeName, change.newValue, options),
        })),
      })),
    }));

    return items;
  } catch (error) {
    return [];
  }
}

export const normalizeAdmissions = (payload: any): { items: AdmissionMapItem[]; total: number } => {
  const data = payload ?? {};
  const content = data.content;
  const total = data.totalElements;

  const normalizeBoolean = (value: unknown): boolean | null => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
    }
    return null;
  };

  try {
    const items: AdmissionMapItem[] = content.map((row: Record<string, any>) => ({
      id: row.id,
      insuranceCarrierName: row.carrierName ?? null,
      contractName: row.contractName ?? null,
      patientName: row.patientName ?? null,
      cidCode: row.cidCode ?? null,
      cidDescription: row.cidDescription ?? null,
      speciality: row.speciality ?? null,
      highCost: row.highCost ?? null,
      unauthorized: normalizeBoolean(row.unauthorized),
      icu: normalizeBoolean(row.icu),
      medicalReport: normalizeAttributeValue(row.medicalReport) ?? null,
      specialCase: normalizeBoolean(row.specialCase),
      passwordStatus: normalizePasswordStatus(row.passwordStatus),
      evolution: row.evolution ?? null,
      note: row.note ?? null,
      urgencyType: row.urgencyType ?? null,
      isCidEditable: row.isCidEditable ?? false,
    }));

    return { items: items, total: total };
  } catch (error) {
    return { items: [], total: 0 };
  }
};

export const defaultFilter = (): AdmissionMapFilter => ({
  financialGroupId: null,
  carrierId: null,
  contractId: null,
  patientName: null,
  hasRecordHistory: null,
  passwordStatuses: [],
  startDate:null,
  endDate: null,
});

export const defaultPagination = (): AdmissionMapPagination => ({
  page: 1,
  itemsPerPage: 20,
  totalItems: 0,
});

export const initState: AdmissionMapModel = {
  filter: defaultFilter(),
  pagination: defaultPagination(),
  sortBy: 'id',
  sortDesc: false,
  loadingPage: false,
  loadingTable: false,
  loadingUpdate: false,
  loadingGroups: false,
  loadingCarriers: false,
  loadingContracts: false,
  loadingPasswordStatuses: false,
  loadingRecordHistories: false,
  loadingExport: false,
  clickAway: false,
  groups: [],
  carriers: [],
  contracts: [],
  records: [],
  recordHistories: [],
  selectedRecords: [],
  passwordStatuses: [],
  urgencyTypes: [],
  highCostTypes: [],
  showSnackbarCustomize: {
    show: false,
    type: null,
    message: '',
  },
    showHistoryModal: {
    show: false,
  },
}
